import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useOrganisme } from '../context/OrganismeContext';
import {
  CalendarDaysIcon,
  MapPinIcon,
  DocumentTextIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  ArrowDownTrayIcon,
  XMarkIcon,
  FunnelIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import HelpButton from '../components/HelpButton';
import { helpContent } from '../data/helpContent';
import FileDropzone from '../components/common/FileDropzone';
import { isAssembleeGenerale, getReunionTypeLabel } from '../utils/reunionTypes';
import {
  REUNION_LIEU_AUTRE,
  REUNION_LIEUX_PREDEFINIS,
  resolveReunionLieuFromForm,
  reunionLieuFormFromStored,
} from '../utils/reunionLieux';

export default function AdminReunions() {
  const { user, permissions } = useAuth();
  const { organismeActif, isSuperAdminMode } = useOrganisme();
  const navigate = useNavigate();
  const canCreateReunion =
    user?.role === 'super_admin' || permissions?.reunions?.createReunion;
  const canEditReunion =
    user?.role === 'super_admin' || permissions?.reunions?.editReunion;
  const canDeleteReunion =
    user?.role === 'super_admin' || permissions?.reunions?.deleteReunion;
  const [reunions, setReunions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  
  // Filtres
  const getDefaultFilters = () => ({
    yearFilter: String(new Date().getFullYear()),
    typeFilter: '',
    organismeFilter: '',
  });

  const [yearFilter, setYearFilter] = useState(getDefaultFilters().yearFilter);
  const [typeFilter, setTypeFilter] = useState('');
  const [organismeFilter, setOrganismeFilter] = useState('');
  const [filtersLoaded, setFiltersLoaded] = useState(false);
  const [availableYears, setAvailableYears] = useState([]);
  
  // Modal création/modification
  const [showModal, setShowModal] = useState(false);
  const [editingReunion, setEditingReunion] = useState(null);
  const [formData, setFormData] = useState({
    date: '',
    heureDebut: '',
    heureFin: '',
    type: 'conseil_syndical',
    lieuSelectKey: '',
    lieuAutre: '',
    notes: '',
    organisme: '',
    exercice: '',
  });
  const [saving, setSaving] = useState(false);
  
  // Liste des organismes disponibles pour l'utilisateur
  const getAvailableOrganismes = () => {
    if (user?.role === 'super_admin') {
      return ['SAR', 'AMAIR'];
    }
    // Pour les admins, retourner leurs organismes
    return user?.organismes || (user?.organisme ? [user.organisme] : []);
  };
  const availableOrganismes = getAvailableOrganismes();
  
  // Modal documents
  const [showDocModal, setShowDocModal] = useState(false);
  const [selectedReunion, setSelectedReunion] = useState(null);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  
  // Modal confirmation suppression
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [reunionToDelete, setReunionToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const hasActiveFilters = () => {
    const defaults = getDefaultFilters();
    return (
      String(yearFilter) !== defaults.yearFilter
      || typeFilter !== defaults.typeFilter
      || organismeFilter !== defaults.organismeFilter
    );
  };

  const resetAllFilters = () => {
    const defaults = getDefaultFilters();
    setYearFilter(defaults.yearFilter);
    setTypeFilter(defaults.typeFilter);
    setOrganismeFilter(defaults.organismeFilter);
    localStorage.removeItem('admin_reunions_filters');
  };

  // Charger les filtres sauvegardés au montage
  useEffect(() => {
    const savedFilters = localStorage.getItem('admin_reunions_filters');
    if (savedFilters) {
      try {
        const filters = JSON.parse(savedFilters);
        const defaults = getDefaultFilters();
        setYearFilter(
          filters.yearFilter !== undefined && filters.yearFilter !== null
            ? String(filters.yearFilter)
            : defaults.yearFilter
        );
        setTypeFilter(filters.typeFilter || '');
        setOrganismeFilter(filters.organismeFilter || '');
      } catch (err) {
        console.error('Erreur lors du chargement des filtres réunions:', err);
      }
    }
    setFiltersLoaded(true);
    generateAvailableYears();
  }, []);

  // Sauvegarder les filtres à chaque modification
  useEffect(() => {
    if (!filtersLoaded) return;

    localStorage.setItem('admin_reunions_filters', JSON.stringify({
      yearFilter,
      typeFilter,
      organismeFilter,
    }));
  }, [yearFilter, typeFilter, organismeFilter, filtersLoaded]);

  useEffect(() => {
    if (!user || !['admin', 'super_admin'].includes(user.role)) {
      navigate('/');
      return;
    }

    if (user.role === 'admin' && !permissions?.reunions?.access) {
      navigate('/admin/dashboard');
      return;
    }

    if (!filtersLoaded) return;

    fetchReunions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, permissions, navigate, yearFilter, typeFilter, organismeFilter, organismeActif, isSuperAdminMode, filtersLoaded]);

  const generateAvailableYears = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let i = currentYear - 5; i <= currentYear + 1; i++) {
      years.push(i);
    }
    setAvailableYears(years);
  };

  const fetchReunions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (yearFilter) params.append('annee', yearFilter);
      if (typeFilter) params.append('type', typeFilter);
      
      // En mode Super Admin, utiliser organismeFilter (filtre manuel)
      // En mode Admin Organisme, utiliser organismeActif
      if (isSuperAdminMode) {
        if (organismeFilter) params.append('organisme', organismeFilter);
      } else if (organismeActif) {
        params.append('organisme', organismeActif);
      }
      
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/reunions?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setReunions(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Erreur lors du chargement des réunions');
    } finally {
      setLoading(false);
    }
  };

  const getTypeLabel = (type, organisme) => getReunionTypeLabel(type, organisme || organismeActif);

  const getTypeBadgeColor = (type) => {
    return isAssembleeGenerale(type)
      ? 'bg-purple-100 text-purple-800' 
      : 'bg-blue-100 text-blue-800';
  };

  const handleOpenCreateModal = () => {
    setEditingReunion(null);
    setFormData({
      date: '',
      heureDebut: '',
      heureFin: '',
      type: 'conseil_syndical',
      lieuSelectKey: '',
      lieuAutre: '',
      notes: '',
      organisme: organismeActif || '',
      exercice: '',
    });
    setShowModal(true);
  };

  const handleOpenEditModal = (reunion) => {
    setEditingReunion(reunion);
    const lieuFields = reunionLieuFormFromStored(reunion.lieu || '');
    setFormData({
      date: reunion.date ? new Date(reunion.date).toISOString().split('T')[0] : '',
      heureDebut: reunion.heureDebut || '',
      heureFin: reunion.heureFin || '',
      type: reunion.type,
      lieuSelectKey: lieuFields.lieuSelectKey,
      lieuAutre: lieuFields.lieuAutre,
      notes: reunion.notes || '',
      organisme: reunion.organisme || '',
      exercice: reunion.exercice || '',
    });
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const lieu = resolveReunionLieuFromForm(formData.lieuSelectKey, formData.lieuAutre);
    if (!lieu) {
      alert('Veuillez choisir un lieu ou renseigner un lieu « Autre ».');
      return;
    }

    const {
      lieuSelectKey: _lieuSelectKey,
      lieuAutre: _lieuAutre,
      ...restForm
    } = formData;
    const payload = { ...restForm, lieu };

    setSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      
      if (editingReunion) {
        const { type, ...updateData } = payload;
        await axios.put(
          `${process.env.REACT_APP_API_URL}/reunions/${editingReunion._id}`,
          updateData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/reunions`,
          payload,
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      
      setShowModal(false);
      fetchReunions();
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleUploadDocument = async (e) => {
    const file = e.target.files[0];
    if (!file || !selectedReunion) return;
    
    setUploadingDoc(true);
    
    try {
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('document', file);
      
      await axios.post(
        `${process.env.REACT_APP_API_URL}/reunions/${selectedReunion._id}/documents`,
        formData,
        { 
          headers: { 
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          } 
        }
      );
      
      // Rafraîchir les documents
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/reunions/${selectedReunion._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedReunion(response.data);
      fetchReunions();
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de l\'upload');
    } finally {
      setUploadingDoc(false);
      e.target.value = '';
    }
  };

  const handleDownloadDocument = async (documentId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/reunions/${selectedReunion._id}/documents/${documentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      window.open(response.data.url, '_blank');
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors du téléchargement');
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/reunions/${selectedReunion._id}/documents/${documentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Rafraîchir les documents
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/reunions/${selectedReunion._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSelectedReunion(response.data);
      fetchReunions();
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la suppression');
    }
  };

  const handleOpenDeleteModal = (reunion) => {
    setReunionToDelete(reunion);
    setShowDeleteModal(true);
  };

  const handleDeleteReunion = async () => {
    if (!reunionToDelete) return;
    
    setDeleting(true);
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/reunions/${reunionToDelete._id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setShowDeleteModal(false);
      setReunionToDelete(null);
      fetchReunions();
    } catch (err) {
      alert(err.response?.data?.message || 'Erreur lors de la suppression');
    } finally {
      setDeleting(false);
    }
  };

  if (loading && reunions.length === 0) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  return (
    <div className="h-full bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Suivi des réunions</h1>
            <p className="mt-1 text-sm text-gray-500">
              Gérez les assemblées générales et conseils syndicaux de votre groupement
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            {!isSuperAdminMode && (
              <HelpButton 
                title={helpContent.reunions.title}
                sections={helpContent.reunions.sections}
              />
            )}
            {canCreateReunion && (
            <button
              onClick={handleOpenCreateModal}
              className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
            >
              <PlusIcon className="h-5 w-5 mr-2" />
              Nouvelle réunion
            </button>
            )}
          </div>
        </div>
      </div>

      {/* Filtres */}
      <div className="px-4 sm:px-6 lg:px-8 py-4 bg-white border-b border-gray-200">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2">
            <FunnelIcon className="h-5 w-5 text-gray-400" />
            <span className="text-sm font-medium text-gray-700">Filtres :</span>
          </div>
          
          <select
            value={yearFilter}
            onChange={(e) => setYearFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">Toutes les années</option>
            {availableYears.map(year => (
              <option key={year} value={year}>{year}</option>
            ))}
          </select>
          
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
          >
            <option value="">Tous les types</option>
            <option value="assemblee_generale_ordinaire">Assemblée générale ordinaire</option>
            <option value="assemblee_generale_extraordinaire">Assemblée générale extraordinaire</option>
            <option value="conseil_syndical">Conseil d'administration / Syndical</option>
            <option value="bureau">Bureau</option>
          </select>
          
          {availableOrganismes.length > 1 && (
            <select
              value={organismeFilter}
              onChange={(e) => setOrganismeFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-indigo-500 focus:border-indigo-500"
            >
              <option value="">Tous les organismes</option>
              {availableOrganismes.map(org => (
                <option key={org} value={org}>{org}</option>
              ))}
            </select>
          )}

          {hasActiveFilters() && (
            <button
              type="button"
              onClick={resetAllFilters}
              className="inline-flex items-center px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-md hover:bg-red-700 transition-colors"
            >
              <XMarkIcon className="h-4 w-4 mr-2" />
              Réinitialiser les filtres
            </button>
          )}

          <div className="text-sm text-gray-500 ml-auto">
            {reunions.length} réunion{reunions.length > 1 ? 's' : ''}
          </div>
        </div>
      </div>

      {/* Contenu */}
      <div className="px-4 sm:px-6 lg:px-8 py-6">
        {error && (
          <div className="mb-6 rounded-lg bg-red-50 p-4">
            <p className="text-sm text-red-700">{error}</p>
          </div>
        )}

        {reunions.length === 0 ? (
          <div className="text-center py-12 bg-white rounded-lg shadow">
            <CalendarDaysIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune réunion</h3>
            <p className="mt-1 text-sm text-gray-500">
              Commencez par créer votre première réunion.
            </p>
            {canCreateReunion && (
            <div className="mt-6">
              <button
                onClick={handleOpenCreateModal}
                className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Nouvelle réunion
              </button>
            </div>
            )}
          </div>
        ) : (
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {user?.role === 'super_admin' && (
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Organisme
                    </th>
                  )}
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                    Lieu
                  </th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                    Documents
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {reunions.map((reunion) => (
                  <tr key={reunion._id} className="hover:bg-gray-50">
                    {user?.role === 'super_admin' && (
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex px-2 py-1 text-xs font-bold rounded bg-gray-100 text-gray-800">
                          {reunion.organisme}
                        </span>
                      </td>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <CalendarDaysIcon className="h-5 w-5 text-gray-400 mr-2" />
                        <span className="text-sm font-medium text-gray-900">
                          {new Date(reunion.date).toLocaleDateString('fr-FR', {
                            weekday: 'long',
                            day: 'numeric',
                            month: 'long',
                            year: 'numeric'
                          })}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getTypeBadgeColor(reunion.type)}`}>
                        {getTypeLabel(reunion.type, reunion.organisme)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap hidden md:table-cell">
                      <div className="flex items-center">
                        <MapPinIcon className="h-4 w-4 text-gray-400 mr-1" />
                        <span className="text-sm text-gray-900">{reunion.lieu}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center hidden sm:table-cell">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                        <DocumentTextIcon className="h-4 w-4 mr-1" />
                        {reunion.documents?.length || 0}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium flex justify-center">
                      <Link
                        to={`/admin/reunions/${reunion._id}`}
                        className="text-green-600 hover:text-green-900 mr-3"
                        title="Voir les détails"
                      >
                        <EyeIcon className="h-5 w-5" />
                      </Link>
                      {canEditReunion && (
                      <button
                        onClick={() => handleOpenEditModal(reunion)}
                        className="text-indigo-600 hover:text-indigo-900 mr-3"
                        title="Modifier"
                      >
                        <PencilIcon className="h-5 w-5" />
                      </button>
                      )}
                      {canDeleteReunion && (
                      <button
                        onClick={() => handleOpenDeleteModal(reunion)}
                        className="text-red-600 hover:text-red-900"
                        title="Supprimer"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            </div>
          </div>
        )}
      </div>

      {/* Modal Création/Modification */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">
                {editingReunion ? 'Modifier la réunion' : 'Nouvelle réunion'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Organisme
                </label>
                <input
                  type="text"
                  value={formData.organisme}
                  disabled
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date *
                </label>
                <input
                  type="date"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    De (heure)
                  </label>
                  <input
                    type="time"
                    value={formData.heureDebut}
                    onChange={(e) => setFormData({ ...formData, heureDebut: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    À (heure)
                  </label>
                  <input
                    type="time"
                    value={formData.heureFin}
                    onChange={(e) => setFormData({ ...formData, heureFin: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Type *
                </label>
                {editingReunion ? (
                  <input
                    type="text"
                    value={getTypeLabel(formData.type, formData.organisme)}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-600 cursor-not-allowed"
                  />
                ) : (
                  <select
                    value={formData.type}
                    onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  >
                    <option value="conseil_syndical">
                      {formData.organisme === 'SAR' ? 'Conseil syndical' : 'Conseil d\'administration'}
                    </option>
                    <option value="assemblee_generale_ordinaire">Assemblée générale ordinaire</option>
                    <option value="assemblee_generale_extraordinaire">Assemblée générale extraordinaire</option>
                    <option value="bureau">Bureau</option>
                  </select>
                )}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Lieu *
                </label>
                <select
                  value={formData.lieuSelectKey}
                  onChange={(e) => setFormData({
                    ...formData,
                    lieuSelectKey: e.target.value,
                    lieuAutre: e.target.value === REUNION_LIEU_AUTRE ? formData.lieuAutre : '',
                  })}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                >
                  <option value="">— Choisir un lieu —</option>
                  {REUNION_LIEUX_PREDEFINIS.map((lieuOption) => (
                    <option key={lieuOption} value={lieuOption}>
                      {lieuOption}
                    </option>
                  ))}
                  <option value={REUNION_LIEU_AUTRE}>Autre</option>
                </select>
                {formData.lieuSelectKey === REUNION_LIEU_AUTRE && (
                  <input
                    type="text"
                    value={formData.lieuAutre}
                    onChange={(e) => setFormData({ ...formData, lieuAutre: e.target.value })}
                    required
                    placeholder="Préciser le lieu"
                    className="mt-2 w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  />
                )}
              </div>

              {/* Exercice (uniquement pour les AG) */}
              {isAssembleeGenerale(formData.type) && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Exercice (optionnel)
                  </label>
                  <input
                    type="number"
                    value={formData.exercice}
                    onChange={(e) => setFormData({ ...formData, exercice: e.target.value })}
                    min="2000"
                    max="2100"
                    placeholder="Année de l'exercice (ex: 2025)"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    L'AG du 21 janvier 2026 peut concerner l'exercice 2025
                  </p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Notes (optionnel)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  rows={3}
                  placeholder="Informations complémentaires, remarques..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-400"
                >
                  {saving ? 'Enregistrement...' : editingReunion ? 'Modifier' : 'Créer'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal Documents */}
      {showDocModal && selectedReunion && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">
                Documents de la réunion
              </h3>
              <button
                onClick={() => setShowDocModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <div className="mb-4 p-3 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">
                <span className="font-medium">{getTypeLabel(selectedReunion.type)}</span>
                {' - '}
                {new Date(selectedReunion.date).toLocaleDateString('fr-FR')}
              </p>
              <p className="text-sm text-gray-500">{selectedReunion.lieu}</p>
            </div>

            {/* Liste des documents */}
            <div className="space-y-2 mb-4 max-h-60 overflow-y-auto">
              {selectedReunion.documents?.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  Aucun document attaché
                </p>
              ) : (
                selectedReunion.documents?.map((doc) => (
                  <div
                    key={doc._id}
                    className="flex items-center justify-between p-3 border border-gray-200 rounded-lg"
                  >
                    <div className="flex items-center min-w-0">
                      <DocumentTextIcon className="h-5 w-5 text-gray-400 mr-2 flex-shrink-0" />
                      <span className="text-sm text-gray-900 truncate">{doc.nomOriginal}</span>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <button
                        onClick={() => handleDownloadDocument(doc._id)}
                        className="text-indigo-600 hover:text-indigo-800"
                        title="Télécharger"
                      >
                        <ArrowDownTrayIcon className="h-5 w-5" />
                      </button>
                      <button
                        onClick={() => handleDeleteDocument(doc._id)}
                        className="text-red-600 hover:text-red-800"
                        title="Supprimer"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            {/* Upload de document */}
            <div className="border-t border-gray-200 pt-4">
              <FileDropzone
                onFileSelect={(file) => handleUploadDocument({ target: { files: [file] } })}
                uploading={uploadingDoc}
                label="Ajouter un document"
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png"
              />
            </div>

            <button
              onClick={() => setShowDocModal(false)}
              className="w-full mt-4 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              Fermer
            </button>
          </div>
        </div>
      )}

      {/* Modal Confirmation Suppression */}
      {showDeleteModal && reunionToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-semibold text-gray-900">
                Confirmer la suppression
              </h3>
              <button
                onClick={() => setShowDeleteModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            <p className="text-gray-600 mb-4">
              Êtes-vous sûr de vouloir supprimer la réunion du{' '}
              <span className="font-medium">
                {new Date(reunionToDelete.date).toLocaleDateString('fr-FR')}
              </span>
              {' '}({getTypeLabel(reunionToDelete.type)}) ?
            </p>
            
            {reunionToDelete.documents?.length > 0 && (
              <p className="text-sm text-honey-600 mb-4">
                ⚠️ Cette réunion contient {reunionToDelete.documents.length} document(s) qui seront également supprimés.
              </p>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteReunion}
                disabled={deleting}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:bg-gray-400"
              >
                {deleting ? 'Suppression...' : 'Supprimer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
